/* eslint-disable no-undef */

import _ from 'lodash';
import { createAxios } from '../lib/axiosFactory';
import Features from '../lib/featureFlags';
import { Balance } from '../lib/interfaces/subscription';
import { errorParser } from '../lib/response/errorParser';
import { VuexLocalStorage } from '../lib/vuexLocalStorage';

interface User {
  id: string | null | undefined;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  autoRedirected: string | null | undefined;
  clientId: string | null | undefined;
  isClientSession: boolean | null | undefined;
  phoneNumber: string;
  countryCode?: string | null | undefined;
  createdAt: string | null | undefined;
  preloadCampaigns: boolean;
  email: string | null | undefined;
  emailVerified: boolean | null | undefined;
  activitiesTracked: string;
  overrideMobileWarning: boolean;
  creditsAvailable: number;
  jobTitle: string | null | undefined;
  accountSetUpCompletedAt: number | null | undefined;
  tier: string | null | undefined;
  balances?: Balance[];
  showEditAutomationDialog: boolean;
  // User settings persisted for the session
  showDefaultFieldsExplainer: boolean;
  pageSizes: any;
}

const storage = new VuexLocalStorage('_USER_');

export const state = function (): User {
  return {
    id: storage.get('id'),
    firstName: storage.get('firstName'),
    lastName: storage.get('lastName'),
    autoRedirected: storage.get('autoRedirected'),
    clientId: storage.get('clientId'),
    isClientSession: storage.get('isClientSession') == 'true',
    phoneNumber: storage.get('phoneNumber'),
    preloadCampaigns: storage.get('preloadCampaigns') == 'true',
    createdAt: storage.get('createdAt'),
    email: storage.get('email'),
    emailVerified: storage.get('emailVerified') == 'true',
    activitiesTracked: storage.get('activitiesTracked'),
    // Temporarily store whether the user should see the default fields explainer.
    // In future this will be persisted and accessible through a user settings page.
    showDefaultFieldsExplainer: true,
    overrideMobileWarning: false,
    creditsAvailable: null,
    jobTitle: storage.get('jobTitle'),
    accountSetUpCompletedAt:
      Number(storage.get('accountSetUpCompletedAt')) || undefined,
    tier: storage.get('tier'),
    balances: undefined,
    countryCode: storage.get('countryCode') || undefined,
    showEditAutomationDialog: false,
    pageSizes: storage.get('pageSizes') || []
  };
};

export const getters = {
  requiresEmailVerification: (
    state: User,
    // eslint-disable-next-line
    getters: any,
    rootState,
    rootGetters
  ): boolean => {
    return (
      !state.emailVerified &&
      !rootGetters['client/isTelkomClient'] &&
      !rootGetters['client/isAppSumoClient']
    );
  },

  isClientSession(state: User) {
    return state.isClientSession;
  },

  authenticated: (state: User): boolean => state.id != null,

  phoneNumber: (state: User): string => {
    return state.phoneNumber;
  },
  isNewUser: (state: User): boolean => {
    const currentTimeInSeconds = Date.now() / 1000;
    const userCreatedAtTimeInSeconds = Number(state.createdAt); // createdAt is in seconds

    const hoursConsideredNew = 24;
    const secondsConsideredNew = hoursConsideredNew * 3600;

    return (
      currentTimeInSeconds - userCreatedAtTimeInSeconds <= secondsConsideredNew
    );
  },

  isInternalUser: (state: User): boolean => {
    return state.email?.toLowerCase().endsWith('mobiz.co');
  },

  preloadCampaigns: (state: User): boolean => {
    return state.preloadCampaigns;
  },

  user: (state: User): User => {
    return state;
  },

  // eslint-disable-next-line @typescript-eslint/ban-types
  activitiesTracked: (state: User): object => {
    return JSON.parse(state.activitiesTracked);
  },

  accountSetUpRequired: (state: User): boolean => {
    return !state.accountSetUpCompletedAt;
  },

  accountManagementAllowed: (
    state: User,
    // eslint-disable-next-line
    getters: any,
    rootState,
    rootGetters
  ): boolean => {
    if (getters.isInternalUser) return true;
    return Features.paidVersionLaunch || rootGetters['client/isTelkomClient'];
  },

  accountStatusBarAllowed: (
    state: User,
    getters: any,
    rootState,
    rootGetters
  ): boolean => {
    if (getters.isInternalUser || rootGetters['client/isTelkomClient'])
      return true;
    return (
      Features.paidVersionLaunch && rootGetters['feature/showGrowthCampaigns']
    );
  },

  balances: (state: User): Balance[] => {
    if (_.isEmpty(state.balances)) return [];

    return state.balances;
  },

  monthlyBalances: (state: User, getters: any): Balance[] => {
    if (_.isEmpty(state.balances)) return [];

    const balances = getters.balances.filter(balance => {
      return ['monthly', 'trial'].includes(balance.creditType);
    });

    return balances;
  },

  yearlyBalances: (state: User, getters: any): Balance[] => {
    if (_.isEmpty(state.balances)) return [];

    const balances = getters.balances.filter(balance => {
      return ['yearly'].includes(balance.creditType);
    });

    return balances;
  },

  payAsYouGoBalances: (state: User, getters: any): Balance[] => {
    if (_.isEmpty(state.balances)) return [];

    return getters.balances.filter(balance => {
      return balance.creditType === 'payAsYouGo';
    });
  },

  orderedBalances: (
    state: User,
    getters: any,
    rootState,
    rootGetters
  ): unknown[] => {
    if (_.isEmpty(state.balances)) return [];

    return getters.balances.slice().sort((a, b) => {
      if (rootGetters['client/isTelkomClient'])
        return a.createdAt - b.createdAt;

      return b.createdAt - b.createdAt;
    });
  },

  sumOfRemainingMonthlyCredits: (state: User, getters: any): number => {
    if (_.isEmpty(state.balances)) return 0;

    return _.sumBy(getters.monthlyBalances, 'quantity');
  },

  sumOfRemainingYearlyCredits: (state: User, getters: any): number => {
    if (_.isEmpty(state.balances)) return 0;

    return _.sumBy(getters.yearlyBalances, 'quantity');
  },

  sumOfInitialMonthlyCredits: (state: User, getters: any): number => {
    if (_.isEmpty(state.balances)) return 0;

    return _.sumBy(getters.monthlyBalances, 'initialQuantity');
  },

  sumOfInitialYearlyCredits: (state: User, getters: any): number => {
    if (_.isEmpty(state.balances)) return 0;

    return _.sumBy(getters.yearlyBalances, 'initialQuantity');
  },

  hasPayAsYouGo: (state: any, getters: any): boolean => {
    return !!getters.payAsYouGoBalances?.length;
  },

  hasBalances: (state: any, getters: any): boolean => {
    return !!getters.balances?.length;
  },

  remainingPayAsYouGoCredits: (state: User, getters: any): number => {
    if (getters.payAsYouGoBalances)
      return _.sumBy(getters.payAsYouGoBalances, 'quantity');

    return 0;
  },

  initialPayAsYouGoCredits: (state: User, getters: any): number => {
    if (getters.payAsYouGoBalances)
      return _.sumBy(getters.payAsYouGoBalances, 'initialQuantity');

    return 0;
  },

  getPageSize: (state: User) => (page: string) => {
    return state.pageSizes[page] || undefined;
  }
};

export const mutations = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  setFirstName(state: User, payload: object): void {
    storage.set(state, payload, 'firstName');
  },

  // eslint-disable-next-line @typescript-eslint/ban-types
  setLastName(state: User, payload: object): void {
    storage.set(state, payload, 'lastName');
  },

  // eslint-disable-next-line @typescript-eslint/ban-types
  setAutoRedirected(state: User, payload: object): void {
    storage.set(state, payload, 'autoRedirected');
  },

  clearSession(state: User): void {
    storage.clear(state);
  },

  // eslint-disable-next-line @typescript-eslint/ban-types
  setId(state: User, payload: object): void {
    storage.set(state, payload, 'id');
  },

  // eslint-disable-next-line @typescript-eslint/ban-types
  setClientId(state: User, payload: object): void {
    storage.set(state, payload, 'clientId');
  },

  setIsClientSession(state: User, payload: boolean): void {
    storage.set(state, payload, 'isClientSession');
  },

  setCreatedAt(state: User, payload: { createdAt: number }): void {
    storage.set(state, payload, 'createdAt');
  },

  // eslint-disable-next-line @typescript-eslint/ban-types
  setPreloadCampaigns(
    state,
    { seededCampaigns }: { seededCampaigns: boolean }
  ): void {
    state.preloadCampaigns = !seededCampaigns;
    storage.set(
      state,
      { preloadCampaigns: !seededCampaigns },
      'preloadCampaigns'
    );
  },

  setPhoneNumber(state, payload: { phoneNumber: string }): void {
    storage.set(state, payload, 'phoneNumber');
  },

  setEmail(state, payload: { email: string }): void {
    storage.set(state, payload, 'email');
  },

  setEmailVerified(state, payload: { emailVerified: boolean }): void {
    storage.set(state, payload, 'emailVerified');
  },

  setActivitiesTracked(
    state,
    { activitiesTrackedObj }: { activitiesTrackedObj: object } // eslint-disable-line @typescript-eslint/ban-types
  ): void {
    const activitiesTracked = JSON.stringify(activitiesTrackedObj);
    const payload = { activitiesTracked };
    storage.set(state, payload, 'activitiesTracked');
  },

  setShowDefaultFieldsExplainer(state, show: boolean): void {
    state.showDefaultFieldsExplainer = show;
  },
  setOverrideMobileWarning(state, show: boolean): void {
    state.overrideMobileWarning = show;
  },

  setCreditsAvailable(state, { credits }): void {
    state.creditsAvailable = credits;
  },

  setJobTitle(state, payload: { jobTitle: string }): void {
    storage.set(state, payload, 'jobTitle');
  },

  setAccountSetUpCompletedAt(
    state,
    payload: { accountSetUpCompletedAt: number }
  ): void {
    storage.set(state, payload, 'accountSetUpCompletedAt');
  },

  setTier(state: User, payload: { tier: string }): void {
    storage.set(state, payload, 'tier');
  },

  setBalances(state: User, payload: { balances: [] }): void {
    storage.set(state, payload, 'balances', true);
  },

  setCountryCode(state: User, payload: { countryCode: string }): void {
    storage.set(state, payload, 'countryCode');
  },

  setShowEditAutomationDialog(state, { show }: { show: boolean }): void {
    state.showEditAutomationDialog = show;
  },
  setPageSize(state: User, payload) {
    state.pageSizes[payload.page] = payload.size;
  }
};

export const actions = {
  //Load user and client information.
  async loadUserAccountDetails(context: any, payload: any) {
    try {
      const response = await createAxios().put(
        'backend/user-registrations-create',
        {
          data: {
            type: 'userRegistrations',
            identityServerUserId: payload.id,
            shop: payload.shop
          }
        }
      );

      context.commit('setId', {
        id: response.data.data.id
      });

      context.commit('setFirstName', {
        firstName: response.data.data.attributes.firstName
      });

      context.commit('setLastName', {
        lastName: response.data.data.attributes.lastName
      });

      context.commit('setClientId', {
        clientId: response.data.data.attributes.clientId
      });

      context.commit('setIsClientSession', {
        isClientSession: response.data.data.attributes.isClientSession
      });

      context.commit('setCreatedAt', {
        createdAt: response.data.data.attributes.createdAt
      });

      context.commit('setPreloadCampaigns', {
        seededCampaigns: !!response.data.data.attributes.seededCampaignsAt
      });

      context.commit('setEmail', {
        email: response.data.data.attributes.email
      });

      context.commit('setEmailVerified', {
        emailVerified: response.data.data.attributes.emailVerified
      });

      context.commit('setActivitiesTracked', {
        activitiesTrackedObj:
          response.data.data.attributes.activitiesTracked ?? {}
      });

      context.commit('setPhoneNumber', {
        phoneNumber: response.data.data.attributes.phone
      });

      context.commit('setJobTitle', {
        jobTitle: response.data.data.attributes.jobTitle
      });

      context.commit('setAccountSetUpCompletedAt', {
        accountSetUpCompletedAt:
          response.data.data.attributes.accountSetUpCompletedAt
      });

      await context.dispatch('loadCreditsAvailable');

      context.commit('setTier', {
        tier: response.data.data.attributes.tier
      });

      const client = response.data.data.included.find(
        (client: any) => client.type === 'clients'
      );

      if (client) {
        context.commit('client/setClient', client, { root: true });
        context.commit(
          'client/setClientSenderPreference',
          {
            clientSenderPreference: client.attributes.clientSenderPreference
          },
          { root: true }
        );
        context.commit(
          'feature/setSubscriptionFeatures',
          {
            subscriptionFeatures: client.attributes.subscriptionFeatures
          },
          { root: true }
        );
      }

      return response.data.data;
    } catch (error) {
      return undefined;
    }
  },
  async getUserDetails({ commit, state }) {
    try {
      const response = await createAxios().get('backend/users-show', {
        params: {
          id: state.id
        }
      });

      commit('setFirstName', {
        firstName: response.data.data.attributes.firstName
      });

      commit('setLastName', {
        lastName: response.data.data.attributes.lastName
      });

      commit('setEmail', {
        email: response.data.data.attributes.email
      });

      commit('setPhoneNumber', {
        phoneNumber: response.data.data.attributes.phone
      });

      commit('setJobTitle', {
        jobTitle: response.data.data.attributes.jobTitle
      });

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async createActivitiesTracked(
    { commit, state },
    { activity }: { activity: string }
  ) {
    try {
      const response = await createAxios().put(
        'backend/user-activities-create',
        {
          data: {
            type: 'userActivity',
            activity: activity
          }
        }
      );

      commit('setActivitiesTracked', {
        activitiesTrackedObj: response.data.data.attributes.activitiesTracked
      });
      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadCreditsAvailable({ commit, state }) {
    try {
      const response = await createAxios().get('backend/credits-show', {
        params: {
          id: state.id
        }
      });

      commit('setCreditsAvailable', {
        credits: response.data.data.attributes.credits
      });

      commit('setBalances', {
        balances: response.data.data.attributes.balances
      });

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async updateUserAccountDetails(context: any, payload: any) {
    try {
      const response = await createAxios().patch(
        'backend/user-registrations-update',
        {
          data: {
            type: 'users',
            attributes: payload
          }
        }
      );

      context.commit('setFirstName', {
        firstName: response.data.data.attributes.firstName
      });
      context.commit('setLastName', {
        lastName: response.data.data.attributes.lastName
      });
      context.commit('setClientId', {
        clientId: response.data.data.attributes.clientId
      });
      context.commit('setCreatedAt', {
        createdAt: response.data.data.attributes.createdAt
      });
      context.commit('setEmail', {
        email: response.data.data.attributes.email
      });
      context.commit('setPhoneNumber', {
        phoneNumber: response.data.data.attributes.phone
      });

      context.commit('setEmailVerified', {
        emailVerified: response.data.data.attributes.emailVerified
      });

      context.commit('setAccountSetUpCompletedAt', {
        accountSetUpCompletedAt:
          response.data.data.attributes.accountSetUpCompletedAt
      });

      context.commit('setJobTitle', {
        jobTitle: response.data.data.attributes.jobTitle
      });

      const client = response.data.data.included.find(
        (client: any) => client.type === 'clients'
      );

      if (client) {
        context.commit('client/setClient', client, { root: true });
        context.commit(
          'feature/setSubscriptionFeatures',
          {
            subscriptionFeatures: client.attributes.subscriptionFeatures
          },
          { root: true }
        );
      }
      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async updateUsers(context: any, payload: any) {
    try {
      const response = await createAxios().patch('backend/users-update', {
        data: {
          type: 'users',
          attributes: payload
        }
      });

      context.commit('setFirstName', {
        firstName: response.data.data.attributes.firstName
      });
      context.commit('setLastName', {
        lastName: response.data.data.attributes.lastName
      });
      context.commit('setClientId', {
        clientId: response.data.data.attributes.clientId
      });
      context.commit('setCreatedAt', {
        createdAt: response.data.data.attributes.createdAt
      });
      context.commit('setEmail', {
        email: response.data.data.attributes.email
      });
      context.commit('setPhoneNumber', {
        phoneNumber: response.data.data.attributes.phone
      });
      context.commit('setAccountSetUpCompletedAt', {
        accountSetUpCompletedAt:
          response.data.data.attributes.accountSetUpCompletedAt
      });
      context.commit('setJobTitle', {
        jobTitle: response.data.data.attributes.jobTitle
      });

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async sendVerificationEmail({ commit, state }) {
    try {
      await createAxios().post('backend/user-verification-emails-create');

      return { success: true, errors: {}, body: {} };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async adminClientSessionCreate(context: any, payload: any) {
    try {
      const response = await createAxios().post(
        'backend/mobiz-admin-client-sessions-create',
        {
          data: {
            type: 'clientSession',
            attributes: payload
          }
        }
      );

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async adminClientSessionDelete() {
    try {
      const response = await createAxios().delete(
        'backend/mobiz-admin-client-sessions-delete',
        {
          data: {
            type: 'clientSession'
          }
        }
      );

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  }
};
